import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogSubtitle,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Modal from '@src/components/common/Modal';
import { SelectionOrderBadge } from '@src/components/common/SelectionOrderBadge';
import { SpeakerFacePile } from '@src/components/common/SpeakerFacePile';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import TopicFilterDropdown from '@src/components/common/TopicFilterDropdown';
import { TOP_LAYER_Z } from '@src/components/common/topLayerZ';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import { clipRangeWords } from '@src/services/reel/clipRangeWords';
import CloseIcon from '@src/svgs/CloseIcon';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { clipCardTag } from '../clipCardTag';
import {
  clipFragmentsFromClips,
  isClipCombinable,
} from '../clipFragmentsFromClips';
import { ClipFilterCheckbox } from './clipFilterCheckbox';
import {
  clipIdentifiedSpeakers,
  clipMatchesFilters,
  clipMatchSnippet,
  clipOpeningText,
  clipSpeakerLabels,
  hasActiveClipFilters,
  highlightSegments,
  NO_CLIP_PICKER_FILTERS,
  SNIPPET_AFTER_RADIUS,
} from './clipPickerFilter';
import { ClipRowPreviewPlayer } from './ClipRowPreviewPlayer';
import { ClipSpeakerFilter } from './ClipSpeakerFilter';
import { normalizeReelQuery } from './reelEditorSearch';
import { sourceFrameThumbnailUrl } from './reelThumbnail';
import { RowPlayToggle, RowPlayTracker } from './RowPlayTracker';
import { spansForTopics } from './storyTopicSpans';

import type { ClipPickerFilters } from './clipPickerFilter';
import type { ClipRowPreviewHandle } from './ClipRowPreviewPlayer';
import type { ClipSpeakerOption } from './ClipSpeakerFilter';
import type { TopicSpans } from './storyTopicSpans';
import type { ReelEditorRow } from './useReelEditor';
import type { DatoSpeaker, OtherVideo, Story } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { BadgeColor } from '@src/utility/badgeColors';

/**
 * How close two source ranges must be to count as the same passage. A quarter
 * of a second is well below the shortest clip anyone saves and well above the
 * frame-snapping the crop applies, so it separates "this clip is already in the
 * reel" from "a different cut that happens to start nearby".
 */
const RANGE_MATCH_TOLERANCE_SECONDS = 0.25;

/**
 * One tooltip instance for the rows' face piles, reel markers and overlap tags.
 * Its own id rather than the panel's or the face pile's shared one, because
 * this dialog is a modal inside the clip-preview modal and both of those tiers
 * sit below its backdrop.
 */
const PICKER_DETAILS_TOOLTIP_ID = 'add-clips-picker-details-tooltip';

/**
 * Where a row's frame starts, measured from the row's edge: its own horizontal
 * padding, the pick badge and the gap after it. The tracker is inset by this
 * rather than by the padding alone, so the playback line starts under the video
 * it belongs to instead of under the checkbox beside it.
 */
/** Three lines of the snippet's own type — what a row shows before it opens. */
const SNIPPET_FONT_SIZE = 10;
const SNIPPET_LINE_HEIGHT = 14;
const SNIPPET_COLLAPSED_HEIGHT = SNIPPET_LINE_HEIGHT * 3;
/** The room the transcript takes around itself once it is open over the rows. */
const SNIPPET_OPEN_PADDING_Y = 8;
const SNIPPET_OPEN_PADDING_X = 10;

const ROW_PADDING_X = 10;
const ROW_GAP = 10;
/** The 16px box plus the 4px its button pads out to a real target either side. */
const PICK_COLUMN_WIDTH = 24;
const FRAME_OFFSET_X = ROW_PADDING_X + PICK_COLUMN_WIDTH + ROW_GAP;

interface AddClipsPickerProps {
  /** The story's other clips — the picker is scoped to this story (DE16). */
  clips: OtherVideo[];
  /** The reel as it stands, for the already-present check and the frame. */
  sections: ReelEditorRow[];
  /** Where the clips will land, phrased for the creator (DS1). */
  destination: string;
  story?: Story | null;
  /**
   * PLAT-11071 DS2 — `single` for a replace, where the gesture is a
   * position-preserving swap: one Section out, one in. Picking a second clip
   * moves the pick rather than adding to it, and the numbered badges collapse to
   * a tick, since one clip has no order to show.
   */
  selectionMode?: 'multi' | 'single';
  /**
   * PLAT-11096 — the story's `spk_X` labels resolved to people, for the speaker
   * filter. Handed down by the panel, which already holds it for the Section
   * rows' face piles, rather than fetched again here (DS4). Without it the filter
   * falls back to the names the clips themselves record.
   */
  speakersByLabel?: Map<string, DatoSpeaker>;
  /** The story's topics, for the topic filter. Empty hides the control (DS10). */
  topics?: string[];
  /**
   * Where each of those topics is spoken in the source. A clip records the
   * prompt it was produced from, not a topic, so this is what the filter
   * actually matches against — see `storyTopicSpans.ts`.
   */
  topicSpans?: TopicSpans;
  /**
   * PLAT-11109 — the *source story's* transcript, for the scrub readout of the
   * row being played: a candidate has no timeline of its own, so its words are
   * sliced out of the story's by source range (`clipRangeWords`). Handed down
   * the way `topicSpans` is, by the surface that owns the transcript. Absent
   * leaves the readout its timecode and no strip under it.
   */
  sourceElements?: readonly TranscriptElement[];
  onClose: () => void;
  /** Confirms with the picked clips, in selection order (DE11). */
  onConfirm: (clips: OtherVideo[]) => void;
}

/**
 * PLAT-11057 DS3 — a clip is already in the reel when *every* passage it would
 * contribute is already there. Matched by source range rather than by clip id:
 * the same passage is often saved twice under different clips, and adding the
 * second one would duplicate the Section rather than add anything.
 */
export function isClipAlreadyInReel(
  clip: OtherVideo,
  sections: readonly ReelEditorRow[],
): boolean {
  const fragments = clipFragmentsFromClips([clip]);
  if (fragments.length === 0) return false;

  return fragments.every(fragment =>
    sections.some(
      section =>
        Math.abs(section.mediaStartTime - fragment.startTime) <=
          RANGE_MATCH_TOLERANCE_SECONDS &&
        Math.abs(section.duration - fragment.duration) <=
          RANGE_MATCH_TOLERANCE_SECONDS,
    ),
  );
}

/**
 * The palette a row's tags draw from: whatever `clipCardTag` can return, so the
 * Reel tag carries the card's own colour, plus the two this picker adds for what
 * the *reel* makes of a clip.
 */
type RowTagColor = BadgeColor | 'orange' | 'gray' | 'yellow';

/** How much of a clip the open reel already holds, and of how much clip. */
export type ClipReelOverlap = {
  /** Length of the clip a Section covers. */
  seconds: number;
  /** The clip's own length — every passage it would contribute, summed. */
  total: number;
  /** `seconds / total`, capped at 1. */
  fraction: number;
};

/**
 * How much of a clip the reel already says — the length of it a Section covers,
 * and how much of the clip that is. `null` where the reel covers none of it.
 *
 * A reel is measured by the clips underneath it, not by the span it occupies:
 * `clipFragmentsFromClips` gives one fragment per `subClips` entry, so both the
 * covered length and the total are sums over its passages, and the gaps between
 * them count as neither. (A reel saved before `subClips` existed carries no
 * passages to read, and falls back to its own range — there is nothing else to
 * measure it by.)
 *
 * A clip *overlaps* the reel when some of what it would contribute is already
 * there without the clip being in the reel outright — a different cut of a
 * passage a Section already covers. Adding it is legitimate (it is a different
 * cut), but it repeats part of what the reel already says, and how much is the
 * whole question: a second of shared audio at a boundary is not the same news as
 * three-quarters of the clip.
 *
 * The covered stretches are merged before they are summed. Sections overlap each
 * other freely — two cuts of one passage sit in plenty of reels — and adding
 * their intersections up unmerged would report more overlap than the clip has
 * seconds. Measured as a real intersection rather than a touch: back-to-back
 * Sections share a boundary, so the same slack the match above allows is what
 * has to be exceeded before this counts as covering anything.
 */
export function clipReelOverlap(
  clip: OtherVideo,
  sections: readonly ReelEditorRow[],
): ClipReelOverlap | null {
  const fragments = clipFragmentsFromClips([clip]);
  let covered = 0;
  let total = 0;

  for (const fragment of fragments) {
    total += fragment.duration;
    const fragmentEnd = fragment.startTime + fragment.duration;
    const spans = sections
      .map(section => ({
        start: Math.max(fragment.startTime, section.mediaStartTime),
        end: Math.min(fragmentEnd, section.mediaStartTime + section.duration),
      }))
      .filter(span => span.end - span.start > RANGE_MATCH_TOLERANCE_SECONDS)
      .sort((a, b) => a.start - b.start);

    // One pass over the sorted spans, extending the open one where the next
    // starts inside it — the merge this needs, without a second array.
    let openEnd = -Infinity;
    for (const span of spans) {
      if (span.start > openEnd) {
        covered += span.end - span.start;
        openEnd = span.end;
      } else if (span.end > openEnd) {
        covered += span.end - openEnd;
        openEnd = span.end;
      }
    }
  }

  if (covered <= 0 || total <= 0) return null;
  return { seconds: covered, total, fraction: Math.min(1, covered / total) };
}

/**
 * What the row's Overlap tag says on hover: how much of the clip the reel
 * already has, and how much of it is new.
 *
 * Both halves, because the second is the one the decision turns on — "20%
 * overlap" leaves the creator working out what they would actually be adding,
 * and "80% are new" answers it. The two are rounded from the same fraction so
 * they always sum to 100, whichever way each one rounds on its own.
 *
 * The lengths follow on a second line, split the same way as the shares above
 * them: the shares are what decide, and the lengths are what they are shares of.
 */
const overlapSummary = (overlap: ClipReelOverlap, isReel: boolean) => {
  const overlapping = Math.round(overlap.fraction * 100);
  return [
    `${overlapping}% of this ${isReel ? 'reel' : 'clip'} overlaps, ${
      100 - overlapping
    }% is new`,
    // `convertTimeToMMSS` is a timecode, so the lengths are stated as times
    // rather than named as minutes — "0:04 minutes" would be four seconds
    // called four minutes.
    `${convertTimeToMMSS(overlap.seconds)} overlapping, ${convertTimeToMMSS(
      Math.max(0, overlap.total - overlap.seconds),
    )} new`,
  ].join('\n');
};

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Text with the searched phrase marked — the row's found line, and the paragraph
 * behind its "More". One definition rather than the same `map` written twice:
 * the two must emphasise identically, since the expanded text is the row's line
 * read in context.
 */
const Highlighted: React.FC<{ text: string; query: string }> = ({
  text,
  query,
}) => (
  <>
    {highlightSegments(text, query).map((segment, index) =>
      segment.isMatch ? (
        // Index keys: the segments are a pure function of this text and query,
        // so a re-split never reorders them — there is no identity to preserve.
        // eslint-disable-next-line react/no-array-index-key
        <SnippetMatch key={index}>{segment.text}</SnippetMatch>
      ) : (
        segment.text
      ),
    )}
  </>
);

/**
 * What a reel row's marker says, and what it says on hover: how much of a reel
 * it is. `null` for an ordinary clip, which contributes one Section and needs no
 * marker.
 *
 * The label and colour come from `clipCardTag`, the clip cards' own rule, rather
 * than being restated here — that rule has already been corrected once (a reel
 * must never be labelled with the prompt `buildReelMetadata` copied off its
 * first clip, PLAT-11049), and a second copy of it would not follow the next
 * correction.
 *
 * The counts are read off the clip's own metadata — passages from `subClips`,
 * chapters from the stored reel (DE12/DE13) — so the row can say it without
 * loading anything. A reel saved before chapters existed reports its Sections
 * alone rather than "0 chapters", which would read as information about the reel
 * rather than about when it was made.
 */
function reelSummary(
  clip: OtherVideo,
): { label: string; color: RowTagColor; summary: string } | null {
  const clipJson = clip.clipJson;
  if (!clipJson?.isReel) return null;

  const tag = clipCardTag(clipJson);
  if (!tag) return null;

  const sections = clipJson.subClips?.length ?? 0;
  const chapters = clipJson.reel?.chapters?.length ?? 0;
  const parts = [
    tag.label,
    plural(sections, 'clip'),
    ...(chapters > 0 ? [plural(chapters, 'chapter')] : []),
  ];
  return { ...tag, summary: parts.join(' · ') };
}

/**
 * PLAT-11057 — picks further clips of the story to add to the open reel.
 *
 * Multi-select, and the order matters: the clips are added in the order they
 * were picked, not the order they appear here (DE11). Clips already in the reel
 * stay on the list but cannot be picked (DS3) — hiding them would leave a
 * creator hunting for a clip that is already in front of them.
 *
 * PLAT-11071 — one picker, three callers: the utility bar, a gap, and a Section
 * being replaced. `selectionMode: 'single'` is the last of those, where the
 * gesture is a swap rather than an addition. The Section's own clip needs no
 * special handling there: it is in the reel, so DS3 already disables it.
 */
export const AddClipsPicker: React.FC<AddClipsPickerProps> = ({
  clips,
  sections,
  destination,
  story,
  selectionMode = 'multi',
  speakersByLabel,
  topics = [],
  topicSpans,
  sourceElements,
  onClose,
  onConfirm,
}) => {
  const isSingle = selectionMode === 'single';

  // Selection order is the add order, so this is an array rather than a Set.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // PLAT-11096 — a way of looking, not a setting (DS9): local state on a picker
  // that unmounts when it closes, so every open starts unfiltered.
  const [query, setQuery] = useState('');
  const [selectedSpeakerIds, setSelectedSpeakerIds] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  // Other reels are out of the way by default too, and for a stronger reason
  // than the used clips below: picking one adds every Section it holds at once,
  // which is a different gesture from adding a clip and rarely the one a creator
  // reaching for "add clips" wants. Still one click away, since combining reels
  // is legitimate.
  const [hideReels, setHideReels] = useState(true);

  // Clips already in the reel are out of the way by default: on a reel of any
  // size most of the list is what has already been added, and the question the
  // picker answers is "what else". They stay one click away rather than gone,
  // since a creator checking whether something is in already needs to see it.
  const [hideUsed, setHideUsed] = useState(true);

  // Overlapping clips start *shown*, unlike the used ones: they can be picked —
  // a different cut of a passage is a real choice — so hiding them by default
  // would take addable clips off a list whose whole job is to offer them.
  const [hideOverlapping, setHideOverlapping] = useState(false);

  const rows = useMemo(
    () =>
      clips.filter(isClipCombinable).map(clip => {
        // Every passage, in playing order — one for an ordinary clip, one per
        // `subClips` entry for a reel. It is what the row's player plays and
        // what its words are sliced by (PLAT-11109); the first of them is also
        // the frame the row shows.
        const fragments = clipFragmentsFromClips([clip]);
        const [fragment] = fragments;
        const alreadyPresent = isClipAlreadyInReel(clip, sections);
        return {
          clip,
          id: clip.id ?? '',
          fragments,
          duration: clip.clipJson?.duration ?? fragment?.duration ?? 0,
          // What the *player* runs to, which on a reel candidate is its
          // passages and not the span they were cut from. The clock the tracker
          // draws against and the words are re-based onto, so it is measured
          // the one way both of those are.
          playDuration: fragments.reduce(
            (total, passage) => total + passage.duration,
            0,
          ),
          thumbnailUrl: sourceFrameThumbnailUrl(
            story,
            fragment?.startTime ?? 0,
          ),
          alreadyPresent,
          // Only ever the clips that are *not* in the reel: a used clip overlaps
          // it by definition, and calling that out twice on one row would say
          // nothing the "Used" tag does not already say.
          overlap: alreadyPresent ? null : clipReelOverlap(clip, sections),
          reel: reelSummary(clip),
          // Who the row shows on its right. Deduplicated by person, since two
          // labels can be one speaker once speakers are combined — and empty
          // where nobody in the clip was ever identified, which shows no pile
          // at all rather than an anonymous placeholder.
          speakers: clipIdentifiedSpeakers(clip, speakersByLabel),
        };
      }),
    [clips, sections, story, speakersByLabel],
  );

  // PLAT-11096 — the people the *listed clips* actually record, so the filter
  // offers nothing that would return an empty list (DS10). One option per
  // person, carrying every label that resolves to them: two labels can be one
  // person once speakers are combined, and selecting the person has to select
  // both (DS4).
  const speakerOptions = useMemo(() => {
    const byId = new Map<string, ClipSpeakerOption>();
    for (const row of rows) {
      for (const label of clipSpeakerLabels(row.clip)) {
        const person = speakersByLabel?.get(label);
        // An unidentified label is keyed by itself and named for the clip that
        // carries it — better than dropping the clip's only speaker out of the
        // filter, which would make it unreachable by speaker at all.
        const id = person?.id ?? label;
        const existing = byId.get(id);
        if (existing) {
          if (!existing.labels.includes(label)) existing.labels.push(label);
          continue;
        }
        byId.set(id, {
          id,
          name: person?.name ?? row.clip.clipJson?.speakerName ?? label,
          headshotUrl: person?.headshots?.[0]?.url,
          labels: [label],
        });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, speakersByLabel]);

  const filters: ClipPickerFilters = useMemo(() => {
    const wanted = new Set(selectedSpeakerIds);
    return {
      query: normalizeReelQuery(query),
      speakerLabels: speakerOptions
        .filter(option => wanted.has(option.id))
        .flatMap(option => option.labels),
      topics: selectedTopics,
      // Resolved once per selection rather than per row: the overlap test below
      // runs for every listed clip on every keystroke.
      topicSpans: spansForTopics(topicSpans, selectedTopics),
    };
  }, [query, selectedSpeakerIds, selectedTopics, speakerOptions, topicSpans]);

  // How many of the story's clips each topic covers, for the dropdown's counts.
  // Over every listed clip rather than what the other filters have left, so the
  // numbers do not move while a creator types — and by the same rule the list
  // itself applies, so a count of 3 is exactly the 3 rows choosing it leaves.
  const topicCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const topic of topics) {
      const spans = spansForTopics(topicSpans, [topic]);
      const topicFilters: ClipPickerFilters = {
        ...NO_CLIP_PICKER_FILTERS,
        topics: [topic],
        topicSpans: spans,
      };
      counts.set(
        topic,
        rows.filter(row => clipMatchesFilters(row.clip, topicFilters)).length,
      );
    }
    return counts;
  }, [rows, topics, topicSpans]);

  /** How many of the listed clips are themselves reels. Same rule: 0 hides the control. */
  const reelCount = useMemo(() => rows.filter(row => row.reel).length, [rows]);

  /** How many of the story's clips the reel already holds — nothing to toggle at 0. */
  const includedCount = useMemo(
    () => rows.filter(row => row.alreadyPresent).length,
    [rows],
  );

  /** And how many repeat a passage it holds without being in it. Same rule: 0 hides the control. */
  const overlappingCount = useMemo(
    () => rows.filter(row => row.overlap).length,
    [rows],
  );

  // Narrowing hides; it never changes what can be picked (DS7) — so this runs
  // *after* `alreadyPresent` is decided, and an already-added clip that matches
  // still shows, greyed out.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        row =>
          !(hideReels && row.reel) &&
          !(hideUsed && row.alreadyPresent) &&
          !(hideOverlapping && row.overlap) &&
          clipMatchesFilters(row.clip, filters),
      ),
    [rows, filters, hideReels, hideUsed, hideOverlapping],
  );

  // PLAT-11109 — which row is previewing, and whether it is running. One at a
  // time (DS3): the player is mounted by the playing row, so starting a second
  // stops the first by unmounting it. Pausing is not stopping, so the two are
  // separate — a paused row keeps its tracker to scrub with.
  const [playingRowId, setPlayingRowId] = useState<string | null>(null);
  const [isRowPlaying, setIsRowPlaying] = useState(false);
  /** Where the playing row has reached, on that clip's own clock. */
  const [playingTime, setPlayingTime] = useState(0);
  const playerRef = useRef<ClipRowPreviewHandle>(null);

  const playingRow = useMemo(
    () => rows.find(row => row.id === playingRowId),
    [rows, playingRowId],
  );

  // DS7 — audio never outlives the row that owns it: a keystroke in the search
  // field can take the playing row off the list. Cleared rather than paused, so
  // clearing the search does not restart a preview the creator walked away from.
  useEffect(() => {
    if (playingRowId && !visibleRows.some(row => row.id === playingRowId)) {
      setPlayingRowId(null);
    }
  }, [playingRowId, visibleRows]);

  const togglePlay = (rowId: string) => {
    if (rowId !== playingRowId) {
      setPlayingRowId(rowId);
      setPlayingTime(0);
    }
    setIsRowPlaying(rowId !== playingRowId ? true : !isRowPlaying);
  };

  // The words under the playing row's readout, sliced out of the source story's
  // transcript and put on that clip's clock. Derived for the one row that has a
  // readout to put them under, so a long story pays nothing to draw the list.
  const playingWords = useMemo(
    () =>
      playingRow ? clipRangeWords(sourceElements, playingRow.fragments) : [],
    [playingRow, sourceElements],
  );

  const toggle = (id: string) =>
    setSelectedIds(current => {
      if (current.includes(id)) {
        return current.filter(selected => selected !== id);
      }
      // A single-select pick moves rather than accumulates.
      return isSingle ? [id] : [...current, id];
    });

  const confirm = () => {
    const byId = new Map(rows.map(row => [row.id, row.clip]));
    const picked = selectedIds
      .map(id => byId.get(id))
      .filter((clip): clip is OtherVideo => !!clip);
    if (picked.length > 0) onConfirm(picked);
  };

  return (
    <Modal isOpen closeModal={onClose}>
      <DialogCard $size="xl" data-testid="add-clips-picker">
        <DialogHeader>
          <DialogTitle>{isSingle ? 'Replace Clip' : 'Add Clips'}</DialogTitle>
          <DialogSubtitle title={destination}>{destination}</DialogSubtitle>
        </DialogHeader>

        {/* Only where there is something to narrow. A filter row over three
            clips is chrome, and each control stands itself down when it has
            nothing behind it (DS10). */}
        {rows.length > 0 &&
          (speakerOptions.length > 0 ||
            topics.length > 0 ||
            reelCount > 0 ||
            includedCount > 0 ||
            overlappingCount > 0) && (
            <FilterStack>
              <Filters data-testid="add-clips-filters">
                <SearchField>
                  <SearchInput
                    type="text"
                    value={query}
                    placeholder="Search titles and transcripts"
                    aria-label="Search clips"
                    data-testid="clip-filter-search"
                    onChange={e => setQuery(e.target.value)}
                  />
                  {/* Clearing puts the list, and every row's opening line, back
                      the way it was — a search is one click from undone. */}
                  {query && (
                    <ClearSearch
                      type="button"
                      aria-label="Clear search"
                      data-testid="clip-filter-search-clear"
                      onClick={() => setQuery('')}
                    >
                      <CloseIcon width="8" height="8" />
                    </ClearSearch>
                  )}
                </SearchField>
                {speakerOptions.length > 0 && (
                  <ClipSpeakerFilter
                    speakers={speakerOptions}
                    selectedIds={selectedSpeakerIds}
                    onApply={setSelectedSpeakerIds}
                  />
                )}
                {topics.length > 0 && (
                  <TopicFilterDropdown
                    topics={topics}
                    selectedTopics={selectedTopics}
                    onApply={setSelectedTopics}
                    clipCounts={topicCounts}
                  />
                )}
              </Filters>

              {/* Under the search rather than beside it: these are statements
                  about the reel, not about the story's clips, and each stands
                  itself down where the reel gives it nothing to hide (DS10).
                  Reels first, at the left: it is the coarsest of the three —
                  what *kind* of thing to list, before what the open reel makes
                  of it. */}
              {(reelCount > 0 || includedCount > 0 || overlappingCount > 0) && (
                <ReelFilters>
                  {reelCount > 0 && (
                    <FilterCheck
                      label={`Hide Reels (${reelCount})`}
                      checked={hideReels}
                      onToggle={() => setHideReels(value => !value)}
                      testId="clip-filter-reels"
                    />
                  )}
                  {includedCount > 0 && (
                    <FilterCheck
                      label={`Hide clips used in this reel (${plural(includedCount, 'clip')})`}
                      checked={hideUsed}
                      onToggle={() => setHideUsed(value => !value)}
                      testId="clip-filter-used"
                    />
                  )}
                  {overlappingCount > 0 && (
                    <FilterCheck
                      label={`Hide Overlapping (${overlappingCount})`}
                      checked={hideOverlapping}
                      onToggle={() => setHideOverlapping(value => !value)}
                      testId="clip-filter-overlapping"
                    />
                  )}
                </ReelFilters>
              )}
            </FilterStack>
          )}

        {rows.length === 0 ? (
          <Empty data-testid="add-clips-empty">
            This story has no other clips to add.
          </Empty>
        ) : visibleRows.length === 0 &&
          includedCount === rows.length &&
          !hasActiveClipFilters(filters) ? (
          // Nothing is left because the reel already holds all of it — a
          // statement about the reel, and one the "filters" wording would
          // misattribute to a filter the creator never set.
          <Empty data-testid="add-clips-all-included">
            Every clip of this story is already in this reel.
          </Empty>
        ) : visibleRows.length === 0 ? (
          // Distinct from the line above, the way the Reel Editor's own search
          // is distinct from an empty reel: "nothing matches" is a statement
          // about the filter, and it has to be clear the clips are still there.
          <Empty data-testid="add-clips-no-matches">
            No clips match these filters.
          </Empty>
        ) : (
          <List role="listbox" aria-multiselectable={!isSingle}>
            {visibleRows.map(row => {
              const order = selectedIds.indexOf(row.id);
              const isPreviewing = row.id === playingRowId;
              // What the clip says, under what it is called. While searching
              // that is the line the query was found in; otherwise its opening
              // words. One `indexOf` per visible row per keystroke.
              // Short before the match, long after it: the phrase sits in the
              // collapsed row's first line, and the rest is what "More" opens
              // the row into.
              const snippet = clipMatchSnippet(
                row.clip,
                filters.query,
                undefined,
                SNIPPET_AFTER_RADIUS,
              );
              const line = snippet
                ? `${snippet.before}${snippet.match}${snippet.after}`
                : clipOpeningText(row.clip);
              return (
                <Row
                  key={row.id}
                  // Not a `<button>` any more (PLAT-11109 DS4): a button may
                  // not nest interactive content, and `disabled` swallows the
                  // clicks of everything inside it — including the play control
                  // an already-used row keeps (DS6). So the pick is guarded in
                  // the handler and `aria-disabled` says so.
                  role="option"
                  tabIndex={0}
                  data-testid={`add-clips-row-${row.id}`}
                  // Said out loud rather than left to the fill: in single-select
                  // mode the badge is a tick, so "which row is picked" is
                  // otherwise only readable as a colour.
                  data-selected={order !== -1}
                  aria-selected={order !== -1}
                  aria-disabled={row.alreadyPresent || undefined}
                  $selected={order !== -1}
                  $disabled={row.alreadyPresent}
                  onKeyDown={e => {
                    // Only the row's own keys. The play control and the tracker
                    // are focusable and inside it, so a bubbled Space would be
                    // taken as a pick — and, worse, `preventDefault`ed out from
                    // under the button it was actually pressed on.
                    if (e.target !== e.currentTarget) return;
                    // What the browser gave the row for free while it was a
                    // button, and what `role="option"` asks for.
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    if (row.alreadyPresent) return;
                    toggle(row.id);
                  }}
                >
                  {/* The pick, ahead of the thumbnail: the leftmost column is
                      where the eye goes down a list checking what it has chosen,
                      and it is the one thing that changes as the creator works.
                      A tick where there is only ever one pick — the number
                      stands for playback position, which a swap does not choose
                      (PLAT-11071 DS2).

                      The box is the *only* thing that picks. A row-wide click
                      target took every click inside it — reading the transcript
                      or reaching for a control checked the clip instead. */}
                  <PickButton
                    type="button"
                    data-testid={`add-clips-pick-${row.id}`}
                    aria-label={order === -1 ? 'Select clip' : 'Deselect clip'}
                    disabled={row.alreadyPresent}
                    onClick={() => toggle(row.id)}
                  >
                    <SelectionOrderBadge
                      position={order === -1 ? null : order + 1}
                      showTick={isSingle}
                    />
                  </PickButton>
                  <Thumbnail>
                    {row.thumbnailUrl ? (
                      <ThumbnailImage
                        src={row.thumbnailUrl}
                        alt=""
                        decoding="async"
                      />
                    ) : (
                      <ThumbnailPlaceholder />
                    )}
                    {/* Only the playing row mounts one (DS3), so the frame is
                        a still on every other row — and nothing plays until it
                        is asked to (DS8). */}
                    {isPreviewing && (
                      <ClipRowPreviewPlayer
                        ref={playerRef}
                        ranges={row.fragments}
                        story={story}
                        poster={row.thumbnailUrl}
                        isPlaying={isRowPlaying}
                        onTimeUpdate={setPlayingTime}
                        // Run out: the row goes back to the still it was found
                        // as, rather than sitting paused at its own end.
                        onEnded={() => setPlayingRowId(null)}
                      />
                    )}
                    {/* On the frame rather than out at the row's end, the way
                        the Section rows carry it: length belongs to the clip,
                        and the right-hand column is then free for who is in
                        it. Stays through hover — it goes only while the row
                        plays, where the tracker says the clock instead. */}
                    <ThumbnailDuration
                      data-thumb-duration
                      $hidden={isPreviewing && isRowPlaying}
                    >
                      {convertTimeToMMSS(row.duration)}
                    </ThumbnailDuration>
                    {/* On every row, an already-used one included: what the
                        reel already holds is exactly what a creator weighing a
                        candidate wants to hear (DS6). Playing is not picking. */}
                    <RowPlayToggle
                      isPlaying={isPreviewing && isRowPlaying}
                      playLabel="Play"
                      testId={`add-clips-play-${row.id}`}
                      onToggle={() => togglePlay(row.id)}
                    />
                  </Thumbnail>
                  <Details>
                    <TitleRow>
                      <RowTitle title={row.clip.title ?? ''}>
                        {row.clip.title || 'Untitled clip'}
                      </RowTitle>
                      {/* A reel adds several Sections at once, so which rows are
                          reels is the difference between adding one passage and
                          adding six — said on the title line, where the eye
                          already is, with the count behind the hover. The same
                          tag the clip cards carry, so a reel reads the same
                          wherever it is listed. */}
                      {row.reel && (
                        <RowTag
                          $color={row.reel.color}
                          data-testid={`add-clips-reel-${row.id}`}
                          data-tooltip-id={PICKER_DETAILS_TOOLTIP_ID}
                          data-tooltip-content={row.reel.summary}
                          aria-label={row.reel.summary}
                        >
                          {row.reel.label}
                        </RowTag>
                      )}
                      {/* What the reel already makes of this clip — in it, or
                          repeating a passage that is. Beside the title, which is
                          where the eye is when it is scanning for what to add,
                          and the same two words the checkboxes above use. The
                          row says it once: the tag replaced the note that used
                          to sit under the title, so a used clip is not described
                          twice on two lines. */}
                      {row.alreadyPresent && (
                        <RowTag
                          $color="gray"
                          data-testid={`add-clips-used-tag-${row.id}`}
                        >
                          Used
                        </RowTag>
                      )}
                      {/* The share on the row, the lengths behind the hover:
                          how much and how long are what decide whether to pick
                          it — neither alone, since 12 seconds is most of a short
                          clip and a corner of a long one — but a title line
                          scanning for what to add has room only for the share,
                          which is the half that decides. */}
                      {row.overlap && (
                        <RowTag
                          $color="yellow"
                          data-testid={`add-clips-overlapping-tag-${row.id}`}
                          data-tooltip-id={PICKER_DETAILS_TOOLTIP_ID}
                          data-tooltip-content={overlapSummary(
                            row.overlap,
                            !!row.reel,
                          )}
                          aria-label={overlapSummary(row.overlap, !!row.reel)}
                        >
                          {Math.round(row.overlap.fraction * 100)}% Overlap
                        </RowTag>
                      )}
                    </TitleRow>
                    {line && (
                      <RowSnippet
                        text={line}
                        query={filters.query}
                        testId={`add-clips-snippet-${row.id}`}
                      />
                    )}
                  </Details>
                  {/* Who is heard in the clip, at the row's right-hand end — the
                      same column the Section rows put their face piles in, so a
                      picked clip reads the same before and after it joins the
                      reel. Absent where nobody in it was ever identified. */}
                  {row.speakers.length > 0 && (
                    <Speakers data-testid={`add-clips-speakers-${row.id}`}>
                      <SpeakerFacePile
                        speakers={row.speakers}
                        tooltipId={PICKER_DETAILS_TOOLTIP_ID}
                      />
                    </Speakers>
                  )}
                  {/* Only under the row being previewed: on every row it would
                      be a scrubber the row does not have, and at 0% on all but
                      one it would read as a rule between them. One timecode,
                      not two — a candidate sits in no reel yet (DS5). */}
                  {isPreviewing && (
                    <RowTracker
                      duration={row.playDuration}
                      progress={
                        row.playDuration > 0
                          ? Math.min(1, playingTime / row.playDuration)
                          : 0
                      }
                      words={playingWords}
                      ariaLabel={`Scrub ${row.clip.title || 'clip'}`}
                      testId={`add-clips-scrub-${row.id}`}
                      readoutTestId={`add-clips-scrub-readout-${row.id}`}
                      trackTestId={`add-clips-progress-${row.id}`}
                      transcriptTestId={`add-clips-scrub-transcript-${row.id}`}
                      onScrub={time => playerRef.current?.seek(time)}
                    />
                  )}
                </Row>
              );
            })}
          </List>
        )}

        {/* The face piles' names, the reel tags' counts and the overlap tags'
            sizes, on the same top layer and for the same reason as the instance
            above. `pre-line` because the overlap tag writes two lines and the
            break between them is the point; the room is so neither wraps into
            three. */}
        <Tooltip
          id={PICKER_DETAILS_TOOLTIP_ID}
          style={{
            ...MODAL_TOOLTIP_STYLE,
            maxWidth: '300px',
            whiteSpace: 'pre-line',
            zIndex: TOP_LAYER_Z.tooltip,
          }}
        />

        <DialogFooter>
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="button"
            data-testid="add-clips-confirm"
            disabled={selectedIds.length === 0}
            onClick={confirm}
          >
            {isSingle
              ? 'Replace with This Clip'
              : selectedIds.length > 1
                ? `Add ${selectedIds.length} Clips`
                : 'Add Clip'}
          </PrimaryButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

/**
 * PLAT-11096 — the filter row, above the list and outside its scroll container,
 * so the two dropdowns open over the clips rather than being clipped by them.
 *
 * Wraps rather than compressing: on a narrow dialog the three controls stack
 * instead of squeezing the search field down to nothing.
 */
const Filters = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
`;

/** The search row and the reel's own checkboxes, stacked. */
const FilterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

/** The two reel checkboxes, side by side under the search field. */
const ReelFilters = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-md);
`;

/**
 * One "hide these" checkbox. Local to the picker rather than the shared
 * `FilterCheckbox`, which puts its label in a `<label>` with nothing to label —
 * only its box is clickable, and a filter row is a whole click target. The
 * indicator matches the picker's own dropdowns (`ClipSpeakerFilter`), so the
 * three controls read as one set.
 */
const FilterCheck: React.FC<{
  label: string;
  checked: boolean;
  onToggle: () => void;
  testId: string;
}> = ({ label, checked, onToggle, testId }) => (
  <FilterCheckButton
    type="button"
    role="checkbox"
    aria-checked={checked}
    data-testid={testId}
    onClick={onToggle}
  >
    <ClipFilterCheckbox checked={checked} />
    {label}
  </FilterCheckButton>
);

const FilterCheckButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  border: none;
  background: none;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  text-align: left;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

/** Takes the row's slack, so the two dropdowns keep their intrinsic width. */
const SearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 140px;
`;

/** The shared close glyph, inset over the field's right edge. */
const ClearSearch = styled.button`
  position: absolute;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  border: none;
  border-radius: var(--radius-button);
  background: none;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  /* Room for the clear button, so a long query does not run under it. After the
     shorthand, which would otherwise reset it. */
  padding-right: 24px;
  border-radius: var(--radius-input);
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-primary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

/* The rows breathe: a 2px gap over 36px thumbnails packed the list tight enough
   that scanning it felt like work. Each row is a decision — a title, a line of
   what is said, its tags and who is in it — so the list is paced for reading
   one at a time rather than for fitting the most on screen. */
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 46vh;
  overflow-y: auto;
  min-height: 0;
  /* The wheel stops at the list's ends rather than carrying on into whatever
     the dialog is covering — a candidate list is scrolled to its bottom often,
     and the page behind moving instead is the reel the creator is building. */
  overscroll-behavior: contain;
  /* The rows stop clear of the scrollbar rather than running under it — it sat
     over the face pile at their right-hand end. Reserved whether or not the
     list scrolls, so a row is the same width either way. */
  scrollbar-gutter: stable;
  padding-right: 8px;
`;

const Empty = styled.div`
  font-size: var(--text-sm);
  color: var(--text-muted);
  padding: var(--spacing-md) 0;
`;

const Row = styled.div<{ $selected: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${ROW_GAP}px;
  width: 100%;
  /* With the padding outside the width, every row ran 20px past the list and
     the face pile at its right-hand end was cut off by the card's edge. */
  box-sizing: border-box;
  /* Taller than the text needs, so the gap between rows reads as space rather
     than as a seam between two tightly-packed blocks. */
  padding: 12px ${ROW_PADDING_X}px;
  border-radius: var(--radius-input);
  background-color: ${props =>
    props.$selected ? 'var(--surface-elevated)' : 'transparent'};
  /* Already in the reel: still listed, still playable, not pickable (DS6). */
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  /* The row is not a click target — its box is (see PickButton). */
  cursor: default;

  ${props =>
    !props.$disabled &&
    `&:hover {
       background-color: var(--surface-elevated);
     }`}

  /* The frame's play/pause reveals on hover (the Section rows' idiom). It
     applies to a used row too — it cannot be picked, but it can be heard. The
     duration badge stays: unlike the Section rows' 64x36 stamp, this frame is
     big enough that the centred control and the corner badge never crowd each
     other, and the length is what a candidate is judged on. */
  &:hover [data-play-toggle] {
    opacity: 1;
  }
`;

/**
 * What picks the clip: the box at the row's head, and nothing else. Padded out
 * to a real target, since the box it holds is 16px.
 */
const PickButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
    border-radius: var(--radius-input);
  }
`;

/**
 * The play tracker, under the whole row.
 *
 * The Section rows hang theirs off the block holding their thumbnail and text,
 * so it stops clear of the face pile; a picker row has no such block, and its
 * pick badge is the leftmost column — so this runs from where the frame starts
 * (`FRAME_OFFSET_X`) to the row's own padding at the far end. Starting it at the
 * row's edge instead put the line under the checkbox, which plays nothing.
 */
const RowTracker = styled(RowPlayTracker)`
  left: ${FRAME_OFFSET_X}px;
  right: ${ROW_PADDING_X}px;
  bottom: 0;
`;

/* 16:9, and taller than the text beside it needs: the frame is what the row is
   scanned by, and at 36px high the video it plays was a stamp. */
const Thumbnail = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 116px;
  height: 65px;
  border-radius: var(--radius-input);
  overflow: hidden;
  background-color: var(--surface-subtle);
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--surface-subtle);
`;

/** The clip's length, on its frame — the Section rows' badge, unchanged. */
const ThumbnailDuration = styled.span<{ $hidden: boolean }>`
  position: absolute;
  right: 3px;
  bottom: 3px;
  padding: 0 4px;
  border-radius: 3px;
  background-color: rgba(0, 0, 0, 0.72);
  color: var(--gray-100);
  font-size: 10px;
  line-height: 14px;
  font-variant-numeric: tabular-nums;
  /* Hover leaves it alone; it goes only while the row plays, where the
     tracker's readout carries the clock. */
  opacity: ${props => (props.$hidden ? 0 : 1)};
  transition: opacity 0.15s ease;
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

/** The heading, with the reel marker held at its end as the title ellipsizes. */
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const RowTitle = styled.span`
  font-size: var(--text-sm);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * A row's tags — reel, used, overlapping — on the clip cards' badge palette, so
 * a reel carries the same tag here that it carries on its card.
 */
const RowTag = styled.span<{ $color: RowTagColor }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background-color: ${props => `var(--badge-${props.$color}-bg)`};
  color: ${props => `var(--badge-${props.$color}-text)`};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
`;

/**
 * What the clip says: its opening words, or the line the search was found in.
 *
 * Three lines at rest, with the rest behind a "More" at the end of the last of
 * them. Opened by a click and never by the pointer passing over it: a list that
 * unfolds a row on hover moves while it is being read, and reading down it is
 * the whole job of this dialog. It closes on "Less" or on a click anywhere
 * outside it, so a creator who has read one and moved on does not have to put
 * it back.
 *
 * The control appears only where there is something behind those lines, and
 * that is measured rather than guessed from the text's length: how much fits is
 * decided by the width the row happens to give it.
 */
const RowSnippet: React.FC<{
  text: string;
  query: string;
  testId: string;
}> = ({ text, query, testId }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const boxRef = useRef<HTMLSpanElement>(null);
  const slotRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = boxRef.current;
    if (!element || expanded) return;
    setIsClamped(element.scrollHeight > element.clientHeight + 1);
  }, [text, query, expanded]);

  // The shared dismiss hook rather than a hand-rolled listener (it also treats
  // focus moving into the renderer iframe as an outside click).
  const collapse = React.useCallback(() => setExpanded(false), []);
  useOutsideAlerter(slotRef, collapse);

  return (
    <SnippetSlot ref={slotRef}>
      <SnippetBox ref={boxRef} $expanded={expanded}>
        <SnippetText data-testid={testId}>
          <Highlighted text={text} query={query} />
        </SnippetText>
        {(isClamped || expanded) && (
          <SnippetToggle
            type="button"
            $expanded={expanded}
            aria-expanded={expanded}
            data-testid={`${testId}-toggle`}
            onClick={() => setExpanded(value => !value)}
          >
            {expanded ? 'Less' : 'More'}
          </SnippetToggle>
        )}
      </SnippetBox>
    </SnippetSlot>
  );
};

/**
 * Reserves the collapsed height so the row keeps it while the text inside
 * opens over the rows below. Without it the whole list would reflow on every
 * expand — the same reason the Clip Producer's card wraps its description in a
 * fixed slot.
 */
const SnippetSlot = styled.span`
  position: relative;
  display: block;
  height: ${SNIPPET_COLLAPSED_HEIGHT}px;
`;

/* Typography and the opening-downward behaviour are the Clip Producer result
   card's `Description`, so a clip's transcript reads the same wherever it is
   previewed: three lines at rest, opening over what is below it, with a surface
   behind it so the rows underneath do not show through. */
const SnippetBox = styled.span<{ $expanded: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: block;
  max-height: ${props =>
    props.$expanded ? '600px' : `${SNIPPET_COLLAPSED_HEIGHT}px`};
  overflow: ${props => (props.$expanded ? 'visible' : 'hidden')};
  color: var(--text-secondary);
  font-size: ${SNIPPET_FONT_SIZE}px;
  font-weight: var(--font-weight-normal);
  line-height: ${SNIPPET_LINE_HEIGHT}px;
  background-color: var(--surface);
  border-radius: 4px;
  z-index: ${props => (props.$expanded ? 3 : 2)};
  transition: max-height var(--transition-normal) ease;

  /* Open, it lies over the rows below, so it has to say where it ends: room
     around the text, an outline and a deeper shadow. The negative margins are
     the same figures back again — the box grows outward into the row's own
     padding and the text stays exactly where it was reading a moment ago. */
  ${props =>
    props.$expanded &&
    `padding: ${SNIPPET_OPEN_PADDING_Y}px ${SNIPPET_OPEN_PADDING_X}px;
     /* One more line's worth of room under the text, so "Less" sits where the
        next line of the transcript would and carries the same padding under it
        as the first line has over it. Cornered against the last sentence and
        the border, it read as crowding the text it ends. */
     padding-bottom: ${SNIPPET_OPEN_PADDING_Y + SNIPPET_LINE_HEIGHT}px;
     margin: -${SNIPPET_OPEN_PADDING_Y + 1}px -${SNIPPET_OPEN_PADDING_X + 1}px;
     border: 1px solid var(--border);
     border-radius: var(--radius-input);
     box-shadow: var(--elevation-md);`}
`;

const SnippetText = styled.span`
  display: block;
`;

/**
 * "More" / "Less" — at the end of the last line rather than on a line of its
 * own, which would cost every row a line of height to say one word. It sits
 * over the text it truncates, so it carries a short fade of the surface into
 * itself and the sentence runs out behind it rather than into it.
 */
const SnippetToggle = styled.button<{ $expanded: boolean }>`
  position: absolute;
  /* Inside the open box's padding rather than out against its border. */
  right: ${props => (props.$expanded ? SNIPPET_OPEN_PADDING_X : 0)}px;
  bottom: ${props => (props.$expanded ? SNIPPET_OPEN_PADDING_Y : 0)}px;
  padding: 0 0 0 20px;
  border: none;
  background: linear-gradient(to right, transparent, var(--surface) 16px);
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: ${SNIPPET_FONT_SIZE}px;
  font-weight: var(--font-weight-medium);
  line-height: ${SNIPPET_LINE_HEIGHT}px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

/**
 * The found phrase inside the line. Gold — the design system's "look here"
 * colour, the same one the playhead marking uses — rather than a highlight fill,
 * which at this size reads as a text field.
 */
const SnippetMatch = styled.mark`
  background: none;
  color: var(--accent);
  font-weight: var(--font-weight-semi-bold);
`;

/** Holds the face pile out of the details' flex stretch. */
const Speakers = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
